import { Spacer } from "@hackthenorth/north";
import { Text, TextInput } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";
import { useClipboard } from "use-clipboard-copy";

import { Icon } from "src/shared/components";
import { TextInputTitle } from "src/views/sponsor/perks/components/Perk/TextComponents";

import type { ShippingAddressField } from "../constants";
import { SHIPPING_ADDRESS, SHIPPING_ADDRESS_FIELDS } from "../constants";

const CopyableField = ({ field }: { field: ShippingAddressField }) => {
  const clipboard = useClipboard();
  const [copied, setCopied] = useState(false);
  const value = SHIPPING_ADDRESS[field.key];

  const onCopy = () => {
    clipboard.copy(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <FieldCell $width={field.width}>
      <TextInputTitle>{field.label}</TextInputTitle>
      <Spacer height={2} />
      <InputWrapper>
        <StyledTextInput
          size="md"
          readOnly
          value={value}
          onChange={() => undefined}
        />
        <CopyButton
          type="button"
          aria-label={`Copy ${field.label}`}
          onClick={onCopy}
        >
          <CopyLabel>{copied ? "Copied!" : "Copy"}</CopyLabel>
          <Icon name="copy" size="h3" />
        </CopyButton>
      </InputWrapper>
      {field.helperText && (
        <>
          <Spacer height={4} />
          <HelperText>{field.helperText}</HelperText>
        </>
      )}
    </FieldCell>
  );
};

export const ShippingAddressForm = () => (
  <FormCard>
    <SectionTitle>Shipping address</SectionTitle>
    <Spacer height={8} />
    <Text>
      Copy each field into your carrier&apos;s shipping form as shown.
    </Text>
    <Spacer height={16} />
    <FieldGrid>
      {SHIPPING_ADDRESS_FIELDS.map((field) => (
        <CopyableField key={field.key} field={field} />
      ))}
    </FieldGrid>
  </FormCard>
);

const FormCard = styled.div`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundWhite};
`;

const SectionTitle = styled(TextInputTitle)`
  font-size: 20px;
`;

const FieldGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const FieldCell = styled.div<{ $width: "full" | "half" | "third" | "quarter" }>`
  flex: ${({ $width }) => {
    switch ($width) {
      case "half":
        return "1 1 calc(50% - 8px)";
      case "third":
        return "1 1 calc(33.333% - 11px)";
      case "quarter":
        return "1 1 calc(25% - 12px)";
      default:
        return "1 1 100%";
    }
  }};
  min-width: ${({ $width }) => {
    switch ($width) {
      case "half":
        return "200px";
      case "third":
        return "140px";
      case "quarter":
        return "120px";
      default:
        return "0";
    }
  }};

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

const CopyLabel = styled.span`
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  transition: max-width 0.15s ease, opacity 0.15s ease;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
  color: ${({ theme }) => theme.globalConstants.color.textBody};

  &:hover {
    color: ${({ theme }) => theme.globalConstants.color.textBody};
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};

    ${CopyLabel} {
      max-width: 72px;
      opacity: 1;
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;

  input {
    cursor: default;
    padding-right: 72px !important;
  }

  &:has(button:hover) input {
    padding-right: 104px !important;
  }
`;

const StyledTextInput = styled(TextInput)`
  width: 100%;
  font-size: 14px !important;
  cursor: default;
`;

const HelperText = styled.span`
  display: block;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;
